import { Button } from '@repo/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from '@repo/ui/dropdown-menu'
import {
	postBackofficeAuthGetMe,
	postBackofficeAuthSignOut
} from '@repo/sdk/gen'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import {
	Archive,
	Box,
	ChevronLeft,
	ChevronsUpDown,
	CircleUserRound,
	Layers3,
	LayoutDashboard,
	LogOut,
	Settings2,
	X,
	ShieldCheck,
	UsersRound
} from 'lucide-react'
import { useTranslation } from '@/lib/i18n'

const navigation = [
	{ icon: LayoutDashboard, key: 'dashboard', to: '/' },
	{ icon: Layers3, key: 'expansions', to: '/expansions' },
	{ icon: Box, key: 'cards', to: '/cards' },
	{ icon: Layers3, key: 'printings', to: '/printings' },
	{ icon: Archive, key: 'reconciliation', to: '/queue' },
	{ icon: UsersRound, key: 'users', to: '/users' },
	{ icon: ShieldCheck, key: 'administrators', to: '/admins' }
] as const

export function BackofficeNavigation(props: {
	isOpen: boolean
	onOpenChange: (isOpen: boolean) => void
}) {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const profileQuery = useQuery({
		queryKey: ['backoffice', 'profile'],
		queryFn: () =>
			postBackofficeAuthGetMe({ body: {} }).then(
				(response) => response.data.data
			)
	})
	const signOutMutation = useMutation({
		mutationFn: () => postBackofficeAuthSignOut({ body: {} }),
		onSuccess: async () => {
			queryClient.clear()
			await navigate({ to: '/login' })
		}
	})
	const profile = profileQuery.data
	const compactClassName = props.isOpen ? '' : 'lg:hidden'
	function closeOnMobileNavigation() {
		if (window.matchMedia('(max-width: 1023px)').matches) {
			props.onOpenChange(false)
		}
	}
	async function openAccount() {
		closeOnMobileNavigation()
		await navigate({ to: '/account' })
	}

	return (
		<>
			{props.isOpen ? (
				<button
					aria-label={t('common:close_navigation')}
					className='fixed inset-0 z-40 bg-foreground/20 lg:hidden'
					onClick={closeOnMobileNavigation}
					type='button'
				/>
			) : null}
			<aside
				className={`fixed inset-y-0 z-50 flex w-72 flex-col border-border border-r bg-card p-5 shadow-xl transition-[transform,width] duration-200 lg:translate-x-0 lg:shadow-none ${props.isOpen ? 'translate-x-0 lg:w-72' : '-translate-x-full lg:w-20'}`}
			>
				<Button
					aria-label={t('common:close_navigation')}
					className='absolute top-3 right-3 lg:hidden'
					onClick={closeOnMobileNavigation}
					size='icon'
					variant='ghost'
				>
					<X />
				</Button>
				<Button
					aria-label={
						props.isOpen
							? t('common:collapse_navigation')
							: t('common:expand_navigation')
					}
					className='absolute top-7 -right-4 hidden rounded-full border bg-card shadow-sm lg:inline-flex'
					onClick={() => props.onOpenChange(!props.isOpen)}
					size='icon'
					variant='outline'
				>
					<ChevronLeft
						className={`transition-transform ${props.isOpen ? '' : 'rotate-180'}`}
					/>
				</Button>
				<div className='mb-10 flex items-center gap-3 px-2'>
					<div className='grid size-10 place-items-center rounded-2xl bg-primary text-primary-foreground shadow-lg shadow-primary/20'>
						<Layers3 className='size-5' />
					</div>
					<div className={compactClassName}>
						<p className='font-semibold tracking-tight'>
							{t('common:app_name')}
						</p>
						<p className='text-muted-foreground text-xs'>
							{t('common:backoffice')}
						</p>
					</div>
				</div>
				<nav className='space-y-1'>
					{navigation.map(({ icon: Icon, key, to }) => (
						<Link
							activeProps={{
								className:
									'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground'
							}}
							className='flex items-center gap-3 rounded-xl px-3 py-2.5 text-muted-foreground text-sm transition-colors hover:bg-accent hover:text-accent-foreground'
							key={key}
							onClick={closeOnMobileNavigation}
							to={to}
						>
							<Icon className='size-4' />
							<span className={compactClassName}>
								{t(`common:${key}`)}
							</span>
						</Link>
					))}
				</nav>
				<DropdownMenu>
					<DropdownMenuTrigger
						render={
							<button
								aria-label={t('common:account_preferences')}
								className='mt-auto flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-popup-open:bg-accent'
								type='button'
							>
								<div className='grid size-9 shrink-0 place-items-center overflow-hidden rounded-full bg-muted text-muted-foreground'>
									{profile?.image?.url ? (
										<img
											alt=''
											className='size-full object-cover'
											height={36}
											src={profile.image.url}
											width={36}
										/>
									) : (
										<CircleUserRound className='size-5' />
									)}
								</div>
								<div
									className={`min-w-0 flex-1 ${compactClassName}`}
								>
									<p className='truncate font-medium text-sm'>
										{profile?.name ?? t('common:account')}
									</p>
									<p className='truncate text-muted-foreground text-xs'>
										{profile?.email ??
											t('common:account_preferences')}
									</p>
								</div>
								<ChevronsUpDown
									className={`size-4 text-muted-foreground ${compactClassName}`}
								/>
							</button>
						}
					/>
					<DropdownMenuContent
						align='start'
						className='w-72 p-1.5'
						side='right'
						sideOffset={8}
					>
						<DropdownMenuGroup>
							<DropdownMenuLabel className='flex items-center gap-3 p-2.5'>
								<div className='grid size-9 shrink-0 place-items-center overflow-hidden rounded-full bg-muted text-muted-foreground'>
									{profile?.image?.url ? (
										<img
											alt=''
											className='size-full object-cover'
											height={36}
											src={profile.image.url}
											width={36}
										/>
									) : (
										<CircleUserRound className='size-5' />
									)}
								</div>
								<div className='min-w-0'>
									<p className='truncate font-medium text-foreground'>
										{profile?.name ?? t('common:account')}
									</p>
									<p className='truncate font-normal text-muted-foreground text-xs'>
										{profile?.email}
									</p>
								</div>
							</DropdownMenuLabel>
							<DropdownMenuSeparator />
							<DropdownMenuItem
								className='min-h-14 items-start gap-3 py-2.5'
								onClick={openAccount}
							>
								<Settings2 className='mt-0.5' />
								<div className='min-w-0'>
									<span className='block font-medium'>
										{t('common:account_preferences')}
									</span>
									<span className='block text-muted-foreground text-xs'>
										{t('common:account_copy')}
									</span>
								</div>
							</DropdownMenuItem>
						</DropdownMenuGroup>
						<DropdownMenuSeparator />
						<DropdownMenuItem
							className='py-2.5 text-destructive focus:bg-destructive/10 focus:text-destructive'
							disabled={signOutMutation.isPending}
							onClick={() => signOutMutation.mutate()}
							variant='destructive'
						>
							<LogOut />
							{t('common:sign_out')}
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</aside>
		</>
	)
}
